import { fileDownloadResponseResolver, jsonResponseResolver } from "./response-resolvers"
import { jsonSerializer, formDataSerializer, urlencodedSerializer } from "./request-serializers"

const DEFAULT_OPTIONS = Object.freeze({
	cache: "no-cache"
})

/**
 * 非同期通信部品
 */
export default class Ajax {
	#defaultOptions = { ...DEFAULT_OPTIONS }

	#basePath = null

	get basePath () {
		return this.#basePath
	}

	/**
	 * @param {string} value
	 */
	set basePath (value) {
		if (!value.endsWith("/")) {
			this.#basePath = value + "/"
		} else {
			this.#basePath = value
		}
	}

	#defaultHeaders = {}

	get defaultHeaders () {
		const defaultHeader = {}
		for (const key in this.#defaultHeaders) {
			const value = this.#defaultHeaders[key]
			if (typeof value === "function") {
				defaultHeader[key] = value()
			} else {
				defaultHeader[key] = value
			}
		}
		return defaultHeader
	}

	#requestSerializers = [
		jsonSerializer,
		formDataSerializer,
		urlencodedSerializer
	]

	#responseResolvers = [
		fileDownloadResponseResolver,
		jsonResponseResolver
	]

	/**
	 * レスポンスのHTTPステータスコードが200番台以外の場合に呼び出されるリスナ
	 * @type {((any:any, response:Response)=>void)[]}
	 */
	#errorHandlers = []
	/**
	 * 通信失敗時に呼び出されるリスナ
	 * @type {(()=>void)[]}
	 */
	#networkErrorHandlers = []
	/**
	 * リクエスト送信前に毎回呼び出されるリスナ
	 * @type {(()=>boolean?)[]}
	 */
	#beforeRequestHandlers = []
	/**
	 * レスポンス受信後に毎回呼び出されるリスナ
	 * @type {((any:any, response:Response)=>void)[]}
	 */
	#afterRequestHandlers = []

	/**
	 * GETリクエストを送信する
	 * @param {string} url
	 * @param {Object} param
	 * @param {Object} options
	 */
	async get (url, params, options = {}) {
		// onBeforeRequestリスナを呼び出し、falseが返却されたら処理終了
		if (this.#beforeRequestHandlers.some(handler => handler() === false)) {
			return
		}

		if (!url.startsWith("/") && this.basePath) {
			url = this.basePath + url
		}
		if (params) {
			const serializer = this.#getSerializer("application/x-www-form-urlencoded")
			url = url + "?" + serializer.serialize(params)
		}

		const headers = {
			...this.defaultHeaders,
			...options.headers
		}
		delete headers["Content-Type"] // GETの場合はコンテンツタイプを指定しない

		options = {
			...this.#defaultOptions,
			...options || {}
		}
		options.method = "GET"
		options.headers = headers

		const response = await fetch(url, options).catch(e => this.#handleNetworkError(e))
		return await this.#processResponse(response)
	}

	/**
	 * POSTリクエストを送信する。
	 * @param {string} url
	 * @param {Object} data
	 * @param {Object} options
	 */
	async post (url, data, options = {}) {
		// onBeforeRequestリスナを呼び出し、falseが返却されたら処理終了
		if (this.#beforeRequestHandlers.some(handler => handler() === false)) {
			return
		}

		if (!url.startsWith("/") && this.basePath) {
			url = this.basePath + url
		}

		const headers = {
			...this.defaultHeaders,
			...options.headers || {}
		}

		const contentType = headers["Content-Type"]
		const serializer = this.#getSerializer(contentType)
		options = {
			...this.#defaultOptions,
			...options || {}
		}
		options.method = "POST"
		options.headers = headers
		options.body = serializer.serialize(data)

		// multipart/form-dataの場合、ヘッダのContentTypeの生成はFetchAPI側に委ねる
		if (contentType === "multipart/form-data") {
			delete options.headers["Content-Type"]
		}

		const response = await fetch(url, options).catch(e => this.#handleNetworkError(e))

		return await this.#processResponse(response)
	}

	/**
	 *
	 * @param {Response} response
	 */
	async #processResponse (response) {
		if (response.ok) {
		// コンテンツタイプを元にレスポンスを処理するリゾルバを探す
			const contentType = response.headers.get("Content-Type")
			if (!contentType) {
				return null
			}
			const hasType = resolver => resolver.type.includes(contentType)
			const resolver = this.#responseResolvers.find(hasType)
			if (!resolver) {
				console.error(`解決できないContent-Typeです。Content-Type=${contentType}`, response)
				this.#afterRequestHandlers.forEach(handler => handler(null, response))
				return
			}

			const responseData = await resolver.resolve(response)

			this.#afterRequestHandlers.forEach(handler => handler(responseData, response))

			return responseData
		} else {
			this.#errorHandlers.forEach(handler => handler(response))
			return Promise.reject(response)
		}
	}

	#handleNetworkError (e) {
		// abortは無視
		if (e.name !== "AbortError") {
			this.#networkErrorHandlers.forEach(handler => handler(e))
		}
		// このまま処理続行できないのでリスローする
		throw e
	}

	#getSerializer (type = "application/x-www-form-urlencoded") {
		const matchType = serializer => serializer.type.join(" ").includes(type)
		const serializer = this.#requestSerializers.find(matchType)
		if (!serializer) throw new Error(`解決できないContent-Typeです。Content-Type=${type}`)
		return serializer
	}

	/**
	 * 通信時にリクエストに付与するデフォルトのヘッダを設定する.
	 * @param {string} name 設定するヘッダのキー
	 * @param {string | () => string)} value 設定するヘッダの値
	 */
	setDefaultHeader (name, value) {
		this.#defaultHeaders[name] = value
	}

	/**
	 * Fetch APIの第二引数initに設定するデフォルト値を設定する.
	 * @param {string} name 設定するオプションのキー
	 * @param {any} value 設定するオプションの値
	 */
	setDefaultOption (name, value) {
		this.#defaultOptions[name] = value
	}

	/**
	 * レスポンスリゾルバを追加する。
	 * @param {string[]} type 解決するコンテンツタイプ
	 * @param {(Response)=>any} resolveFunc レスポンスを解決する関数
	 */
	addResponseResolver (type, resolveFunc) {
		this.#responseResolvers.push({
			type,
			resolve: async response => {
				const resolved = resolveFunc(response)
				if (resolved instanceof Promise) {
					return await resolved
				}
				return resolved
			}
		})
	}

	/**
	 * リクエスト前に呼び出される
	 * @param {()=>boolean?} listener
	 */
	onBeforeRequest (listener) {
		if (listener) this.#beforeRequestHandlers.push(listener)
	}

	/**
	 * リクエスト後に呼び出される
	 * @param {(any, response:Response)=>void} listener
	 */
	onAfterRequest (listener) {
		if (listener) this.#afterRequestHandlers.push(listener)
	}

	/**
	 * レスポンスのHTTPステータスコードが200番台以外の場合に呼び出される
	 * @param {(any, response:Response)=>void} listener
	 */
	onError (listener) {
		if (listener) this.#errorHandlers.push(listener)
	}

	/**
	 * ネットワーク接続でエラーが発生した場合に呼び出される
	 * @param {(e:Error)=>void} listener
	 */
	onNetoworkError (listener) {
		if (listener) this.#networkErrorHandlers.push(listener)
	}
}
